import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Reveal } from '../animations/Reveal';
import {
    FaLock,
    FaTruck,
    FaShieldAlt,
    FaArrowRight,
    FaTag,
    FaCheckCircle,
    FaChevronDown,
    FaChevronUp,
    FaTimes,
    FaChevronRight,
    FaTrash
} from 'react-icons/fa';

const CartSummary = ({
                         subtotal,
                         shipping,
                         discountTotal,
                         total,
                         itemCount,
                         promoCode,
                         onPromoCodeChange,
                         onApplyPromoCode,
                         onCheckout,
                         isLoading,
                         appliedCartOffers = [],
                         compact = false,
                         onRemoveOffer = null
                     }) => {
    const isFreeShipping = shipping === 0;
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoSuccess, setPromoSuccess] = useState(false);
    const [isExpanded, setIsExpanded] = useState(!compact);
    const [showPromoInput, setShowPromoInput] = useState(false);
    const [removingOffer, setRemovingOffer] = useState(false);

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;

        setPromoLoading(true);
        try {
            await onApplyPromoCode();
            setPromoSuccess(true);
            setTimeout(() => {
                setPromoSuccess(false);
                if (compact) setShowPromoInput(false);
            }, 3000);
        } catch (error) {
            console.error('Error applying promo:', error);
            setPromoSuccess(false);
        } finally {
            setPromoLoading(false);
        }
    };

    const handleRemoveAppliedOffer = async () => {
        if (!onRemoveOffer) return;

        setRemovingOffer(true);
        try {
            await onRemoveOffer();
        } catch (error) {
            console.error('Error removing offer:', error);
        } finally {
            setRemovingOffer(false);
        }
    };

    // Calculate original total before discount
    const originalTotal = subtotal + shipping;

    return (
        <Reveal delay={0.3}>
            <div className={`bg-white rounded-xl sm:rounded-2xl shadow-subtle overflow-hidden ${
                compact ? 'p-4' : 'p-4 sm:p-6'
            }`}>
                {/* Header with Expand/Collapse for mobile */}
                {compact ? (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center justify-between w-full mb-4"
                        disabled={isLoading}
                    >
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-medium text-charcoal">Order Summary</h2>
                            <span className="text-sm text-charcoal/40">({itemCount} items)</span>
                        </div>
                        {isExpanded ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                    </button>
                ) : (
                    <div className="mb-6">
                        <h2 className="text-xl font-medium text-charcoal mb-2">
                            Order Summary
                        </h2>
                        <p className="text-sm text-charcoal/60">
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                        </p>
                    </div>
                )}

                {/* Content - Collapsible on mobile */}
                {(isExpanded || !compact) && (
                    <>
                        {/* Breakdown */}
                        <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                            {/* Subtotal */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm sm:text-base text-charcoal/70">Subtotal</span>
                                <span className="font-medium text-charcoal">
                                    ${subtotal.toFixed(2)}
                                </span>
                            </div>

                            {/* Applied Offers Section */}
                            {appliedCartOffers.length > 0 && (
                                <div className="space-y-2 border-t border-nude/30 pt-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-charcoal">Applied Offers</span>
                                        {onRemoveOffer && (
                                            <button
                                                onClick={handleRemoveAppliedOffer}
                                                disabled={removingOffer}
                                                className="text-xs text-charcoal/40 hover:text-red-500 flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {removingOffer ? (
                                                    <>
                                                        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        Removing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaTrash className="w-3 h-3" />
                                                        Remove All
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {appliedCartOffers.map((offer, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className="flex justify-between items-center p-3 bg-gradient-to-r from-sage/5 to-sage/10 rounded-xl border border-sage/20"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FaTag className="w-3 h-3 text-sage" />
                                                <div>
                                                    <span className="text-sm font-medium text-sage block">
                                                        {offer.offer?.code || offer.code}
                                                    </span>
                                                    <p className="text-xs text-charcoal/60 mt-1">
                                                        {offer.offer?.title || 'Discount applied'}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="font-medium text-sage">
                                                -${(offer.discountAmount || 0).toFixed(2)}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            )}

                            {/* Discount Total (if no individual offers shown) */}
                            {discountTotal > 0 && appliedCartOffers.length === 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm sm:text-base text-charcoal/70">Discount</span>
                                    <span className="font-medium text-green-600">
                                        -${discountTotal.toFixed(2)}
                                    </span>
                                </div>
                            )}

                            {/* Shipping */}
                            <div className="flex justify-between items-center pt-2 border-t border-nude/30">
                                <div className="flex items-center gap-2">
                                    <FaTruck className={`w-3 h-3 sm:w-4 sm:h-4 ${isFreeShipping ? 'text-sage' : 'text-charcoal/40'}`} />
                                    <span className="text-sm sm:text-base text-charcoal/70">Shipping</span>
                                </div>
                                <div className="text-right">
                                    {isFreeShipping ? (
                                        <span className="text-sage font-medium flex items-center gap-1 text-sm sm:text-base">
                                            <FaCheckCircle className="w-3 h-3" />
                                            Free
                                        </span>
                                    ) : (
                                        <span className="font-medium text-charcoal">
                                            ${shipping.toFixed(2)}
                                        </span>
                                    )}
                                    {subtotal < 100 && shipping > 0 && (
                                        <p className="text-xs text-charcoal/40 mt-1">
                                            Add ${(100 - subtotal).toFixed(2)} for free shipping
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Promo Code Section */}
                            <div className="pt-3 sm:pt-4 border-t border-nude/30">
                                {compact ? (
                                    // Mobile optimized promo section
                                    <div className="space-y-3">
                                        {promoSuccess ? (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="flex items-center justify-between p-3 bg-sage/10 border border-sage/30 rounded-xl"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FaCheckCircle className="w-4 h-4 text-sage" />
                                                    <span className="text-sm font-medium text-sage">
                                                        Promo code applied!
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        onPromoCodeChange('');
                                                        setPromoSuccess(false);
                                                    }}
                                                    className="p-1 hover:bg-sage/20 rounded-full"
                                                >
                                                    <FaTimes className="w-3 h-3 text-sage" />
                                                </button>
                                            </motion.div>
                                        ) : showPromoInput ? (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                className="space-y-3"
                                            >
                                                <div className="flex gap-2">
                                                    <input
                                                        value={promoCode}
                                                        onChange={(e) => {
                                                            onPromoCodeChange(e.target.value);
                                                            setPromoSuccess(false);
                                                        }}
                                                        placeholder="Enter promo code"
                                                        className="flex-1 px-4 py-3 bg-nude/20 border border-nude/30 rounded-xl text-sm placeholder:text-charcoal/40 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/30"
                                                        autoFocus
                                                        disabled={promoLoading}
                                                    />
                                                    <button
                                                        onClick={() => setShowPromoInput(false)}
                                                        className="px-3 py-3 text-charcoal/40 hover:text-charcoal rounded-xl"
                                                        disabled={promoLoading}
                                                    >
                                                        <FaTimes className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => setShowPromoInput(false)}
                                                        className="flex-1 px-4 py-3 bg-white border border-nude/30 text-charcoal/60 rounded-xl font-medium text-sm hover:bg-nude/20 transition-colors"
                                                        disabled={promoLoading}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleApplyPromo}
                                                        disabled={!promoCode.trim() || promoLoading}
                                                        className="flex-1 px-4 py-3 bg-champagne text-white rounded-xl font-medium text-sm hover:bg-champagne/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        {promoLoading ? (
                                                            <span className="inline-flex items-center justify-center">
                                                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                            </span>
                                                        ) : 'Apply'}
                                                    </button>
                                                </div>
                                            </motion.div>
                                        ) : (
                                            <button
                                                onClick={() => setShowPromoInput(true)}
                                                className="w-full flex items-center justify-between p-3 bg-nude/20 border border-nude/30 rounded-xl hover:bg-nude/30 transition-colors group"
                                                disabled={promoLoading}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FaTag className="w-4 h-4 text-charcoal/40 group-hover:text-champagne" />
                                                    <span className="text-sm font-medium text-charcoal/60 group-hover:text-charcoal">
                                                        Add promo code
                                                    </span>
                                                </div>
                                                <FaChevronRight className="w-3 h-3 text-charcoal/40 group-hover:text-champagne" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    // Desktop promo section
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <div className="flex-1 relative">
                                            <input
                                                value={promoCode}
                                                onChange={(e) => {
                                                    onPromoCodeChange(e.target.value);
                                                    setPromoSuccess(false);
                                                }}
                                                placeholder="Enter promo code (e.g., SAVE10)"
                                                className="w-full px-4 py-3 bg-nude/20 border border-nude/30 rounded-xl text-sm placeholder:text-charcoal/40 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/30 pr-24"
                                                disabled={promoLoading}
                                            />
                                            {promoSuccess && (
                                                <p className="mt-2 text-sm text-sage flex items-center gap-1">
                                                    <FaCheckCircle /> Promo code applied successfully!
                                                </p>
                                            )}
                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                <motion.div
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    <button
                                                        onClick={handleApplyPromo}
                                                        disabled={!promoCode.trim() || promoLoading}
                                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-champagne to-champagne/80 text-white text-xs font-medium hover:from-champagne/90 hover:to-champagne/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {promoLoading ? (
                                                            <span className="inline-flex items-center">
                                                                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"/>
                                                                Applying...
                                                            </span>
                                                        ) : promoSuccess ? (
                                                            'Applied!'
                                                        ) : (
                                                            'Apply Code'
                                                        )}
                                                    </button>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Total Section */}
                        <div className="py-4 sm:py-6 border-t border-nude/50 mb-4 sm:mb-6">
                            <div className="flex justify-between items-center">
                                <div className="space-y-1">
                                    <span className="text-lg sm:text-xl font-medium text-charcoal">Total</span>
                                    <p className="text-xs text-charcoal/40">
                                        Including taxes where applicable
                                    </p>
                                </div>
                                <div className="text-right">
                                    {discountTotal > 0 && (
                                        <div className="text-sm text-charcoal/60 mb-1 line-through">
                                            ${originalTotal.toFixed(2)}
                                        </div>
                                    )}
                                    <div className="text-2xl sm:text-3xl font-light text-charcoal">
                                        ${total.toFixed(2)}
                                    </div>
                                    {discountTotal > 0 && (
                                        <div className="text-sm text-sage font-medium mt-1">
                                            You saved ${discountTotal.toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Checkout Button */}
                        <div className="mb-4 sm:mb-6">
                            {compact ? (
                                <motion.div whileTap={{ scale: 0.98 }}>
                                    <button
                                        onClick={onCheckout}
                                        disabled={isLoading || itemCount === 0}
                                        className="w-full px-6 py-4 bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/90 text-pearl rounded-full font-medium text-base hover:from-charcoal/90 hover:via-charcoal/80 hover:to-charcoal/70 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden shadow-lg"
                                    >
                                        <span className="relative flex items-center justify-center gap-3">
                                            {isLoading ? (
                                                <>
                                                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <FaLock className="w-4 h-4" />
                                                    Proceed to Checkout
                                                    <FaArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </span>
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <button
                                        onClick={onCheckout}
                                        disabled={isLoading || itemCount === 0}
                                        className="w-full px-8 py-4 rounded-full bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/90 text-pearl font-medium text-lg hover:from-charcoal/90 hover:via-charcoal/80 hover:to-charcoal/70 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
                                    >
                                        <span className="relative flex items-center justify-center gap-3">
                                            {isLoading ? (
                                                <>
                                                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <FaLock className="w-5 h-5"/>
                                                    Proceed to Secure Checkout
                                                    <motion.span
                                                        className="group-hover:translate-x-1 transition-transform"
                                                        animate={{ x: [0, 5, 0] }}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                    >
                                                        <FaArrowRight className="w-5 h-5"/>
                                                    </motion.span>
                                                </>
                                            )}
                                        </span>
                                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                                    </button>
                                </motion.div>
                            )}
                        </div>

                        {/* Security Assurance */}
                        <Reveal delay={0.4}>
                            <div className="pt-4 sm:pt-6 border-t border-nude/30">
                                <div className="flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-charcoal/40">
                                    <FaShieldAlt className="w-3 h-3 sm:w-4 sm:h-4 text-sage" />
                                    <span>Secure SSL encryption • Your data is protected</span>
                                </div>
                            </div>
                        </Reveal>
                    </>
                )}
            </div>
        </Reveal>
    );
};

export default CartSummary;